import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, BookOpen, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TutorialsCard, { TutorialsCardData } from '@/components/partner/TutorialsCard';
import TutorialsViewModal from '@/components/partner/TutorialsViewModal';
import TutorialsEditModal from '@/components/partner/TutorialsEditModal';

interface TutorialsCardFromDB {
  id: number;
  title: string;
  description?: string;
  content: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

// Sortable wrapper for TutorialsCard
function SortableTutorialsCard({
  card,
  onEdit,
  onDelete,
  onView
}: {
  card: TutorialsCardFromDB;
  onEdit: (card: TutorialsCardData) => void;
  onDelete: (id: number) => void;
  onView: (card: TutorialsCardData) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <TutorialsCard
        id={card.id}
        title={card.title}
        description={card.description}
        content={card.content}
        display_order={card.display_order}
        is_published={card.is_published}
        created_at={card.created_at}
        updated_at={card.updated_at}
        mode="admin"
        onEdit={onEdit}
        onDelete={onDelete}
        onView={onView}
        dragHandleProps={listeners}
      />
    </div>
  );
}

function TutorialsManager() {
  const [cards, setCards] = useState<TutorialsCardFromDB[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<TutorialsCardData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Check if user has admin role
  const { isAdmin, isLoading: adminCheckLoading } = useIsAdmin();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load cards
  const loadCards = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('tutorials-cards', {
        method: 'GET',
      });

      if (error) throw error;
      setCards(data?.cards || []);
    } catch (error) {
      console.error('Error loading cards:', error);
      toast.error('Failed to load tutorials cards');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && !adminCheckLoading) {
      loadCards();
    } else if (!adminCheckLoading) {
      setIsLoading(false);
    }
  }, [isAdmin, adminCheckLoading]);

  // Create new card
  const handleCreateCard = () => {
    setSelectedCard(null);
    setEditModalOpen(true);
  };

  // Edit existing card
  const handleEditCard = (card: TutorialsCardData) => {
    setSelectedCard(card);
    setEditModalOpen(true);
  };

  // View card
  const handleViewCard = (card: TutorialsCardData) => {
    setSelectedCard(card);
    setViewModalOpen(true);
  };

  // Delete card
  const handleDeleteCard = async (id: number) => {
    if (!confirm('Are you sure you want to delete this tutorial?')) return;

    try {
      const { error } = await supabase.functions.invoke('tutorials-cards', {
        method: 'DELETE',
        body: { id },
      });

      if (error) throw error;

      toast.success('Tutorial deleted successfully');
      loadCards();
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error('Failed to delete tutorial');
    }
  };

  // Save card (create or update)
  const handleSaveCard = async (cardData: { title: string; description?: string; content: string; is_published: boolean }) => {
    setIsSaving(true);
    try {
      const method = selectedCard ? 'PUT' : 'POST';
      const body = selectedCard
        ? { id: selectedCard.id, ...cardData }
        : cardData;

      const { error } = await supabase.functions.invoke('tutorials-cards', {
        method,
        body,
      });

      if (error) throw error;

      toast.success(selectedCard ? 'Tutorial updated successfully' : 'Tutorial created successfully');
      setEditModalOpen(false);
      loadCards();
    } catch (error) {
      console.error('Error saving card:', error);
      toast.error('Failed to save tutorial');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = cards.findIndex((card) => card.id === active.id);
      const newIndex = cards.findIndex((card) => card.id === over.id);

      const newCards = arrayMove(cards, oldIndex, newIndex);

      // Update display_order for all affected cards
      const cardOrders = newCards.map((card, index) => ({
        id: card.id,
        display_order: index,
      }));

      setCards(newCards);

      try {
        const { error } = await supabase.functions.invoke('tutorials-cards', {
          method: 'PATCH',
          body: { cardOrders },
        });

        if (error) throw error;
      } catch (error) {
        console.error('Error reordering cards:', error);
        toast.error('Failed to reorder tutorials');
        // Revert the change
        loadCards();
      }
    }
  };

  // Show access denied message if user is not admin
  if (!adminCheckLoading && !isAdmin) {
    return (
      <div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-destructive">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Access Denied
            </CardTitle>
            <CardDescription>
              This page is only accessible to administrators
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                Administrator Access Required
              </h3>
              <p className="text-muted-foreground mb-4">
                The Tutorials Manager is only accessible to users with administrator privileges.
                You need to have an active subscription with the admin plan to access this page.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading || adminCheckLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading tutorials cards...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <BookOpen className="h-5 w-5 mr-2" />
                Tutorials Manager
              </CardTitle>
              <CardDescription>
                Create and manage tutorial cards for co-branded partners. Cards are displayed in order and can be published or kept as drafts.
              </CardDescription>
            </div>
            <Button onClick={handleCreateCard}>
              <Plus className="h-4 w-4 mr-2" />
              Create New Tutorial
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {cards.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No tutorials yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first tutorial to get started.
              </p>
              <Button onClick={handleCreateCard}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Tutorial
              </Button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={cards.map(card => card.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cards.map((card) => (
                    <SortableTutorialsCard
                      key={card.id}
                      card={card}
                      onEdit={handleEditCard}
                      onDelete={handleDeleteCard}
                      onView={handleViewCard}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <TutorialsViewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        card={selectedCard}
        mode="admin"
      />

      <TutorialsEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        card={selectedCard}
        onSave={handleSaveCard}
        isLoading={isSaving}
      />
    </div>
  );
}

export default TutorialsManager;